/**
 * @file Handles all configurations for AHD Fleet.
 * @author Phillip Kühn
 */
import { FleetCarKategorie, InfoGridFuelCategory } from '@ahd/shared/types/ahd-fleet-api/fleet-car/enums';
import { Lang } from '@ahd/shared/types/ahd-fleet-api/lang';
import { FilterOrder, FilterSort } from '@ahd/shared/types/ahd-fleet-api/mobile/AdQuery';

import { ViewOptionsView } from '../hooks/use-car-search/types';
import { type ConfigObject, DebugLevel, ModuleViewType, TrackingProvider } from './types';

const configDefault: ConfigObject = {
    general: {
        acceptMapsExternal: false,
        debug: DebugLevel.NONE,
        enable: false,
        idGroup: undefined,
        isLandingpage: false,
        language: Lang.deDE,
        metaTitlePrefix: undefined,
        metaTitleSeperator: ' | ',
        metaTitleSuffix: undefined,
        mockup: false,
        projectName: undefined,
        searchPath: '/fahrzeugsuche',
        searchPathDark: undefined,
        searchPathSearch: undefined,
        ahdWeb: false,
        useLocationAlias: true,
        disableSeoRoutes: false,
        useCanonicalLinks: false,
        useTakeinSort: false,

        // Fleet Converter Settings
        disableFinancing: false,
        disableLeasing: false,
        upeInsteadOfVk: false,
        upeInsteadOfVkn: false,
        useEyeCatcher: false,
    },
    contact: {
        blacklistLeadsystem: undefined,
        buttons: {
            invertColors: false,
            offsetX: 2,
            offsetY: 2,
            position: 'right',
        },
        carSearchFilters: ['hersteller', 'modell', 'ezVon', 'motor'],
        catchCustomFields: undefined,
        cta: undefined,
        isLeadSystem: undefined,
        isCatchLeadSystem: undefined,
        linkDisclaimer: undefined,
        phoneRequired: false,
        useTextInsteadOfPrivacyCheckBox: undefined,
    },
    api: {
        autoUncle: {
            departmentIds: undefined,
            id: undefined,
            origin: undefined,
            priceAlert: false,
            priceRating: false,
            sourceName: undefined,
            tradeIn: false,
        },
        bdk: {
            enabled: true,
            bdkRetailerId: undefined,
            hasNoBdkOrderDraft: false,
        },
        itTectureUcl: {
            enabled: false,
        },
        tracking: {
            apiKey: null,
            customScripts: {
                commonSuccess: true,
                ctaClick: undefined,
                formCommonSuccess: undefined,
                mailClick: undefined,
                mapsClick: undefined,
                printClick: undefined,
                priceAlertClick: undefined,
                phoneClick: undefined,
                shareClick: undefined,
                tradeInClick: undefined,
                websiteClick: undefined,
            },
            enabled: true,
            provider: TrackingProvider.Octo,
        },
        webcalc: {
            enabled: true,
            domain: 'HEY.CAR.ERNST.DEALER',
        },
    },
    module: {
        aktionsslider: true,
        erweiterteSchnellsuche: true,
        fahrzeugliste: true,
        fahrzeugslider: true,
        fahrzeugsuche: true,
        finanzierungsansicht: true,
        schnellsuche: true,
        standorte: false,
    },
    aktionsslider: {
        adjustColNumberByListLength: false,
        defaultFilter: undefined,
        defaultFilterId: undefined,
        defaultSort: undefined,
        forcedFilter: {
            sort: FilterSort.Zufall,
            special: true,
        },
        forcedDefaultFilter: undefined,
        limitCols: 4,
        noAnimations: false,
        viewType: ModuleViewType.NORMAL,
    },
    cartile: {
        infoGrid: {
            big: {
                [FleetCarKategorie.BUS]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.CARAVAN]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.MOTORRAD]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.REISEMOBIL]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.TRANSPORTER]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                default: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: ['ez', 'km', '[L]ident', 'engine.leistung', 'engine.motor', 'getriebe', '[L]farbe', 'antrieb'],
                },
            },
            small: {
                [FleetCarKategorie.BUS]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.CARAVAN]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.MOTORRAD]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: null,
                },
                [FleetCarKategorie.REISEMOBIL]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.TRANSPORTER]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                default: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: ['verbrauch.text'],
                },
            },
            list: {
                [FleetCarKategorie.BUS]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.CARAVAN]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.MOTORRAD]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.REISEMOBIL]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.TRANSPORTER]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                default: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: null,
                },
            },
        },
        labels: ['availableAt', 'autoaboAvailableAt', 'aktion', 'commercial', 'leasing'],
    },
    erweiterteSchnellsuche: {
        adjustColNumberByListLength: false,
        buttonBelowFilters: false,
        defaultFilter: undefined,
        defaultFilterId: undefined,
        defaultSort: { order: FilterOrder.DESC, sort: FilterSort.Views },
        filter: ['hersteller', 'modell', 'ezVon', 'motor'],
        forcedFilter: {
            moreCars: 1,
            pageSize: 15,
        },
        forcedDefaultFilter: undefined,
        limitCols: 4,
        noAnimations: false,
        oneFilterPerRow: false,
        viewType: ModuleViewType.NORMAL,
    },
    fahrzeugliste: {
        defaultFilter: undefined,
        defaultFilterId: undefined,
        defaultSort: { order: FilterOrder.DESC, sort: FilterSort.Views },
        forcedFilter: {
            moreCars: 1,
            pageSize: 9,
        },
        forcedDefaultFilter: undefined,
        limitCols: 3,
        noAnimations: false,
        viewType: ModuleViewType.NORMAL,
    },
    fahrzeugslider: {
        adjustColNumberByListLength: false,
        defaultFilter: undefined,
        defaultFilterId: undefined,
        defaultSort: { order: FilterOrder.DESC, sort: FilterSort.Views },
        filter: ['hersteller', 'modell'],
        forcedFilter: {
            moreCars: 1,
            pageSize: 15,
        },
        forcedDefaultFilter: undefined,
        limitCols: 4,
        noAnimations: false,
    },
    fahrzeugsuche: {
        customNameSofort: undefined,
        defaultFilter: undefined,
        defaultFilterId: undefined,
        defaultSort: { order: FilterOrder.DESC, sort: FilterSort.Views },
        detailsSingleRow: false,
        filter: {
            sidebar: ['tags', 'aktion', 'sofort', 'hersteller', 'modell', 'aufbau', 'typ', 'km', 'preis', 'rate', 'ez', 'motor', 'getriebe', 'aus'],
            top: ['[SM]hersteller', '[SM]modell', '[LG]tags', '[LG]typ', 'aktion', 'sofort', 'aufbau', 'motor', 'getriebe', 'km', 'preis', 'rate', 'ez', 'aus'],
        },
        firstImageIndex: undefined,
        forcedFilter: {
            pageSize: 18,
        },
        forcedDefaultFilter: undefined,
        hideContactPersons: false,
        hideFooterAutohausDigitalLink: false,
        hideFooterVerbrauch: false,
        hideHeading: true,
        hideProbefahrt: false,
        idExposee: 1521,
        limitCols: 3,
        listView: ViewOptionsView.Grid,
        locationInfo: ['fax', 'googleMaps', 'mail', 'opening', 'phone', 'website'],
        noAnimations: false,
        originalH1: undefined,
        shareButtons: undefined,
        titleWithDesign: false,
        topFilter: false,
        tradeInLink: undefined,
        useLocationMap: false,
        useRawGoogleMapsColors: false,
        viewType: ModuleViewType.PAGING,
    },
    fahrzeugdetail: {
        additionalInfo: [],
        hideVerbrauch: false,
        infoGrid: {
            big: {
                [FleetCarKategorie.BUS]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.CARAVAN]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.MOTORRAD]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: ['ident', 'typ', 'ez', 'km', 'engine.motor', 'getriebe', 'engine.leistung', 'farbe', 'engine.hubraum', 'standort', 'siegel_garantie', 'antrieb'],
                },
                [FleetCarKategorie.REISEMOBIL]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.TRANSPORTER]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                default: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: ['ident', 'typ', 'ez', 'km', 'engine.motor', 'getriebe', 'engine.leistung', 'farbe', 'tueren', 'sitze', 'siegel', 'antrieb'],
                },
            },
            small: {
                [FleetCarKategorie.BUS]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.CARAVAN]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.MOTORRAD]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: null,
                },
                [FleetCarKategorie.REISEMOBIL]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.TRANSPORTER]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                default: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: ['verbrauch.combined', 'verbrauch.emission', 'verbrauch.co2class'],
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: [
                        'verbrauch.primarySecondaryCombinedWeighted',
                        'verbrauch.emissionWeighted',
                        'verbrauch.effizienzWeighted',
                        'verbrauch.combinedDischarged',
                        'verbrauch.emissionDischarged',
                        'verbrauch.effizienzDischarged',
                    ],
                    [InfoGridFuelCategory.DEFAULT]: ['verbrauch.combined', 'verbrauch.emission', 'verbrauch.co2class'],
                },
            },
            list: {
                [FleetCarKategorie.BUS]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.CARAVAN]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.MOTORRAD]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.REISEMOBIL]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                [FleetCarKategorie.TRANSPORTER]: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: undefined,
                },
                default: {
                    [InfoGridFuelCategory.CELL]: undefined,
                    [InfoGridFuelCategory.ELECTRO]: undefined,
                    [InfoGridFuelCategory.GAS]: undefined,
                    [InfoGridFuelCategory.PLUGINHYBRID]: undefined,
                    [InfoGridFuelCategory.OTHERHYBRID]: undefined,
                    [InfoGridFuelCategory.BIFUEL]: undefined,
                    [InfoGridFuelCategory.DEFAULT]: null,
                },
            },
        },
        orderTabs: [],
        showLocationImage: true,
        similarCarsFilter: {},
        variant: 1,
    },
    finanzierungsansicht: {
        defaultFilter: undefined,
        defaultFilterId: undefined,
        defaultSort: { order: FilterOrder.ASC, sort: FilterSort.Rate },
        filter: ['monatlicheRateButton', 'sortButton'],
        forcedFilter: {
            fd: ['fin'],
            moreCars: 1,
            pageSize: 12,
        },
        forcedDefaultFilter: undefined,
        limitCols: 4,
        noAnimations: false,
        viewType: ModuleViewType.NORMAL,
    },
    schnellsuche: {
        buttonBelowFilters: false,
        defaultFilter: undefined,
        defaultFilterId: undefined,
        defaultSort: undefined,
        filter: ['hersteller', 'modell'],
        forcedFilter: {
            onlyFacetts: true,
        },
        forcedDefaultFilter: undefined,
        limitCols: 4,
        noAnimations: false,
        noBorderPadding: false,
        oneFilterPerRow: false,
    },
    standorte: {
        defaultFilter: undefined,
        defaultFilterId: undefined,
        defaultSort: undefined,
        forcedFilter: {
            onlyFacetts: true,
        },
        forcedDefaultFilter: undefined,
        limitCols: undefined,
        noAnimations: undefined,
        useRawGoogleMapsColors: false,
    },
    style: {
        borderRadius: 2,
        darkMode: false,
        disableSliderShadow: false,
        fontFamily: null,
        fontSize: 16,
        muiComponents: {},
        muiTypography: {},
        noAnimations: false,
        primaryColor: '#0d1b48',
        scrollOffset: undefined,
        secondaryColor: '#666666',
    },
    cookies: {
        analysis: false,
        functional: false,
        marketing: false,
        necessary: true,
    },
};

export default configDefault;
